import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CloudDownload, Download, FileText, FileX } from 'lucide-react';
import { ErroRequisicao, mensagemDeErro } from '../../nucleo/api';
import { nomeDeArquivo } from '../../nucleo/formato';
import { useDiscreto } from '../../componentes/sensivel';
import { TextoCortado } from '../../componentes/texto-cortado';
import { tamanhoArquivo, urlAnexo, type AnexoMensagem } from './tipos';

/**
 * U11 (lote 1): baixa pelo script o anexo que ficou no provedor, para mostrar o motivo quando ele não vem (o e-mail saiu do
 * provedor, o conector caiu) em vez de uma página de erro do navegador.
 */
export async function baixarAnexoDoProvedor(url: string, nome: string) {
  const r = await fetch(url, { credentials: 'same-origin' });
  if (!r.ok) {
    const corpo = (await r.json().catch(() => null)) as { code?: string; message?: string } | null;
    throw new ErroRequisicao(r.status, { code: corpo?.code ?? (r.status >= 500 ? 'servidor.indisponivel' : 'erro'), message: corpo?.message ?? '' });
  }
  const link = document.createElement('a');
  link.href = URL.createObjectURL(await r.blob());
  link.download = nome;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(link.href), 60_000);
}

function AnexoNoProvedor({ url, nome, tamanho }: { url: string; nome: string; tamanho: number }) {
  const { t, i18n } = useTranslation('conversas');
  const [baixando, setBaixando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  async function baixar() {
    setBaixando(true);
    setErro(null);
    try {
      await baixarAnexoDoProvedor(url, nome);
    } catch (e) {
      setErro(mensagemDeErro(e));
    } finally {
      setBaixando(false);
    }
  }
  return (
    <li className="flex min-w-0 flex-col gap-1" data-anexo-provedor>
      <button
        type="button"
        onClick={baixar}
        disabled={baixando}
        aria-busy={baixando}
        aria-label={baixando ? t('u11.baixando') : t('u11.baixarDoProvedor', { nome })}
        className="flex w-fit max-w-full min-w-0 items-center gap-2 rounded-lg border border-borda bg-sup-1 px-2.5 py-1.5 text-left text-xs text-tinta-2 hover:text-tinta-1 disabled:opacity-60"
      >
        <CloudDownload className="size-4 shrink-0" aria-hidden />
        <TextoCortado className="min-w-0">{nome}</TextoCortado>
        {tamanho > 0 && <span className="numero shrink-0 text-tinta-3">{tamanhoArquivo(tamanho, i18n.language)}</span>}
      </button>
      <span className="text-xs text-tinta-3">{baixando ? t('u11.baixando') : t('u11.noProvedorAnexo')}</span>
      {erro && (
        <p role="alert" className="text-xs break-words text-perigo">
          {erro}
        </p>
      )}
    </li>
  );
}

/**
 * Fase 2 (W9, F1c): anexos de uma mensagem. Imagem vira miniatura (abre em outra aba), áudio e vídeo tocam, documento
 * baixa. O arquivo vem pela rota do Conversas (escopo da fila) e sai sempre como anexo, com o tipo conferido na entrada.
 * No modo discreto, a miniatura fica de fora (só o nome, censurado) para a imagem não aparecer na tela compartilhada.
 */
export function AnexosMensagem({ conversaId, anexos, nomes = [] }: { conversaId: string; anexos: AnexoMensagem[]; nomes?: (string | null | undefined)[] }) {
  const { t, i18n } = useTranslation('conversas');
  const { ativo } = useDiscreto();
  if (!anexos.length) return null;
  return (
    <ul className="flex min-w-0 flex-col gap-2">
      {anexos.map((a) => {
        const url = urlAnexo(conversaId, a.id);
        const nome = nomeDeArquivo(a.nome || a.id.slice(0, 8), ...nomes);
        if (a.expurgado)
          return (
            <li key={a.id} className="flex w-fit max-w-full min-w-0 items-center gap-2 rounded-lg border border-borda px-2.5 py-1.5 text-xs text-tinta-3" data-anexo-expurgado>
              <FileX className="size-4 shrink-0" aria-hidden />
              <TextoCortado className="min-w-0">{nome}</TextoCortado>
              <span className="shrink-0">{t('u11.anexoRemovido')}</span>
            </li>
          );
        if (a.sob_demanda) return <AnexoNoProvedor key={a.id} url={url} nome={nome} tamanho={a.tamanho} />;
        return (
          <li key={a.id} className="flex min-w-0 flex-col gap-1.5">
            {a.familia === 'imagem' && !ativo && (
              <a href={url} target="_blank" rel="noopener noreferrer" aria-label={t('w9.abrirImagem', { nome })} className="block w-fit max-w-full">
                <img src={url} alt={nome} loading="lazy" className="max-h-60 max-w-full rounded-lg border border-borda object-contain" />
              </a>
            )}
            {a.familia === 'audio' && !ativo && <audio controls preload="none" src={url} className="w-full max-w-xs" aria-label={nome} />}
            {a.familia === 'video' && !ativo && <video controls preload="none" src={url} className="max-h-60 max-w-full rounded-lg border border-borda" aria-label={nome} />}
            <a href={url} download className="flex w-fit max-w-full min-w-0 items-center gap-2 rounded-lg border border-borda bg-sup-1 px-2.5 py-1.5 text-xs text-tinta-2 hover:text-tinta-1" aria-label={t('w9.baixar', { nome })}>
              {a.familia === 'documento' ? <FileText className="size-4 shrink-0" aria-hidden /> : <Download className="size-4 shrink-0" aria-hidden />}
              <TextoCortado className="min-w-0">{nome}</TextoCortado>
              {a.tamanho > 0 && <span className="numero shrink-0 text-tinta-3">{tamanhoArquivo(a.tamanho, i18n.language)}</span>}
            </a>
          </li>
        );
      })}
    </ul>
  );
}
